import React from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { WalletController } from '@/api/WalletController';
import { useWallet } from '@/stores/wallet.store';

interface MemeSwapListStore {
  walletGuid: string | null;
  memeSwapList: any[];
  isLoading: boolean;
  error: string | null;
  noWalletsError: boolean;
  lastFetched: number | null;
  previousWalletGuid: string | null;
  setWalletGuid: (walletGuid: string) => void;
  fetchList: () => Promise<void>;
  refetch: () => Promise<void>;
  resetState: () => void;
  syncWithCurrentWallet: () => void;
  _lastFetch?: number; // Used for in-memory throttling
}

export const useMemeSwapListStore = create<MemeSwapListStore>()(
  persist(
    (set, get) => ({
      walletGuid: null,
      memeSwapList: [],
      isLoading: false,
      error: null,
      noWalletsError: false,
      lastFetched: null,
      previousWalletGuid: null,
      setWalletGuid: (walletGuid: string) => {
        const currentWalletGuid = get().walletGuid;
        // Only update if the wallet has actually changed
        if (walletGuid !== currentWalletGuid) {
          set({ 
            walletGuid, 
            previousWalletGuid: currentWalletGuid,
            memeSwapList: [],
            error: null,
            isLoading: true,
            _lastFetch: 0 // Reset throttle so next fetch is not throttled
          });
          // Immediately reload the list for the new wallet
          get().fetchList();
        }
      },
      resetState: () => set({ 
        memeSwapList: [], 
        error: null, 
        noWalletsError: false,
        isLoading: false,
        lastFetched: null 
      }),
      syncWithCurrentWallet: () => {
        const { currentWallet } = useWallet.getState();
        const walletGuid = currentWallet?.guid || null;
        const currentStoreGuid = get().walletGuid;
        
        if (walletGuid !== currentStoreGuid) {
          if (walletGuid) {
            set({ _lastFetch: 0 }); // Reset throttle
            get().setWalletGuid(walletGuid);
            get().fetchList();
          } else {
            // If no wallet is connected, reset the state
            set({ walletGuid: null, memeSwapList: [], error: null, noWalletsError: false, isLoading: false });
          }
        }
      },
      fetchList: async () => {
        const { walletGuid } = get();
        if (!walletGuid) {
          set({ error: 'No walletGuid set', memeSwapList: [], noWalletsError: false, isLoading: false });
          return;
        }

        // --- Throttle API calls to avoid 429 errors ---
        const now = Date.now();
        if ((get()._lastFetch ?? 0) && now - (get()._lastFetch ?? 0) < 500) {
          console.warn('[MemeSwap] Throttled: fetchList called too soon after previous fetch. Skipping API call.');
          return;
        }
        set({ _lastFetch: now });
        // ------------------------------------------------
        
        // Don't set isLoading to true if we already have data
        // This prevents UI flickering when refreshing
        const hasExistingData = get().memeSwapList.length > 0;
        if (!hasExistingData) {
          set({ isLoading: true });
        }
        
        set({ error: null, noWalletsError: false });
        
        try {
          // Store the wallet GUID we're fetching for to handle race conditions
          const fetchingForGuid = walletGuid;
          const data = await WalletController.getMemeSwapList(walletGuid);
          
          // Check if wallet changed during fetch to avoid showing stale data
          if (fetchingForGuid !== get().walletGuid) {
            console.log('[MemeSwap] Wallet changed during fetch, discarding results');
            return;
          }
          
          console.log('[MemeSwap] Server response for memeSwapList:', data);
          console.log('[MemeSwap] Wallet GUID used for fetch:', walletGuid);
          console.log('[MemeSwap] Current wallet state:', useWallet.getState().currentWallet);
          
          // If API returns { data: [...], ... }, use data.data; else use data
          const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
          
          // Check if the API returned no wallets
          if (data === null || (Array.isArray(data) && data.length === 0) || 
              (data?.data === null) || (Array.isArray(data?.data) && data?.data.length === 0)) {
            console.log('[MemeSwap] No wallets returned from API');
            console.log('[MemeSwap] Data type:', typeof data);
            console.log('[MemeSwap] Is array:', Array.isArray(data));
            console.log('[MemeSwap] Data length:', Array.isArray(data) ? data.length : 'N/A');
            
            // Set the error state but don't clear the list if we already have data
            // This prevents flickering when API temporarily fails
            const existingList = get().memeSwapList;
            if (existingList.length > 0) {
              console.log('[MemeSwap] Keeping existing list of', existingList.length, 'items');
              set({ noWalletsError: true, isLoading: false, lastFetched: Date.now() });
            } else {
              set({ memeSwapList: [], noWalletsError: true, isLoading: false, lastFetched: Date.now() });
            }
          } else {
            console.log('[MemeSwap] Successfully fetched', list.length, 'meme swaps');
            set({ memeSwapList: list, noWalletsError: false, isLoading: false, lastFetched: Date.now() });
          }
        } catch (e: any) {
          // Only update error if the wallet hasn't changed
          if (walletGuid === get().walletGuid) {
            set({ 
              error: e?.message || 'Failed to fetch meme swap list', 
              noWalletsError: e?.message === 'No wallets returned from API',
              isLoading: false 
            });
          }
        }
      },
      refetch: async () => {
        await get().fetchList();
      },
    }),
    {
      name: 'meme-swap-list-store',
      partialize: (state) => ({
        walletGuid: state.walletGuid,
        memeSwapList: state.memeSwapList,
        lastFetched: state.lastFetched,
      }),
    }
  )
);

// Hook for polling (e.g., in a component)
export function useMemeSwapListPolling(intervalMs = 10000) {
  const refetch = useMemeSwapListStore((s) => s.refetch);
  const syncWithCurrentWallet = useMemeSwapListStore((s) => s.syncWithCurrentWallet);
  const { currentWallet, externalWallet } = useWallet();
  
  // Initial sync with wallet
  React.useEffect(() => {
    syncWithCurrentWallet();
  }, [syncWithCurrentWallet]);
  
  // Sync when wallet changes
  React.useEffect(() => {
    syncWithCurrentWallet();
  }, [currentWallet?.guid, externalWallet?.guid, syncWithCurrentWallet]);
  
  // Regular polling
  React.useEffect(() => {
    // Initial fetch
    refetch();
    
    const interval = setInterval(() => {
      refetch();
    }, intervalMs);
    
    return () => clearInterval(interval);
  }, [refetch, intervalMs]);
}
